// ScanTimeline — checklist sequencial do scan, agrupado por categoria OWASP.
//
// Um nó por check; o estado (pending/running/pass/finding/error) decide o
// marcador e a cor. É o "o que está sendo executado" que o operador pediu:
// risca o que passou, destaca o achado, deixa o pendente em cinza.

import { Box, Group, Loader, Text, Timeline } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconCircle, IconExclamationCircle } from '@tabler/icons-react'

import { COLOR } from '../../../recursos/components/HardRestart/tokens'
import type { ScanCheck, ScanSeverity } from './types'

const SEV_PT: Record<Exclude<ScanSeverity, null>, string> = {
  low: 'baixo',
  medium: 'médio',
  high: 'alto',
  critical: 'crítico',
}

function sevColor(sev: ScanSeverity): string {
  if (sev === 'critical' || sev === 'high') return COLOR.danger
  if (sev === 'medium') return COLOR.warning
  return COLOR.brandVerde
}

function bulletFor(c: ScanCheck): { node: React.ReactNode; color: string } {
  switch (c.status) {
    case 'pass':
      return { node: <IconCheck size={12} />, color: COLOR.brandVerde }
    case 'finding':
      return { node: <IconAlertTriangle size={12} />, color: sevColor(c.severity) }
    case 'error':
      return { node: <IconExclamationCircle size={12} />, color: COLOR.muted }
    case 'running':
      return { node: <Loader size={12} color={COLOR.brandAzul} />, color: COLOR.brandAzul }
    default: // pending
      return { node: <IconCircle size={10} />, color: COLOR.muted }
  }
}

function metaFor(c: ScanCheck): string | null {
  if (c.status === 'finding') {
    const qtd = `${c.findings_count} ${c.findings_count === 1 ? 'achado' : 'achados'}`
    const sev = c.severity ? SEV_PT[c.severity] : null
    return sev ? `${qtd} · ${sev}` : qtd
  }
  if (c.status === 'error') return c.error ? `não avaliado — ${c.error}` : 'não avaliado'
  if (c.status === 'running') return 'em execução…'
  return null
}

function agruparPorCategoria(checks: ScanCheck[]): Array<[string, ScanCheck[]]> {
  const ordem: string[] = []
  const mapa = new Map<string, ScanCheck[]>()
  for (const c of checks) {
    const cat = c.category || '—'
    if (!mapa.has(cat)) { mapa.set(cat, []); ordem.push(cat) }
    mapa.get(cat)!.push(c)
  }
  return ordem.map(cat => [cat, mapa.get(cat)!])
}

const TERMINAL = new Set(['pass', 'finding', 'error'])

export function ScanTimeline({ checks }: { checks: ScanCheck[] }) {
  const grupos = agruparPorCategoria(checks)
  return (
    <Box>
      {grupos.map(([cat, itens]) => {
        const ativos = itens.filter(c => TERMINAL.has(c.status)).length
        return (
          <Box key={cat} mb="md">
            <Text size="xs" fw={700} c="dimmed" ff="monospace" mb={6} style={{ letterSpacing: '0.04em' }}>
              {cat}
            </Text>
            <Timeline active={ativos - 1} bulletSize={20} lineWidth={2}>
              {itens.map(c => {
                const b = bulletFor(c)
                const meta = metaFor(c)
                const riscado = c.status === 'pass'
                return (
                  <Timeline.Item
                    key={c.check_id}
                    data-check={c.check_id}
                    data-status={c.status}
                    bullet={<span style={{ color: b.color, display: 'flex' }}>{b.node}</span>}
                    color={b.color}
                    title={
                      <Group gap={8} wrap="nowrap">
                        <Text size="sm" style={{ opacity: riscado ? 0.65 : 1 }}>{c.title}</Text>
                        {meta && (
                          <Text size="xs" fw={600} style={{ color: b.color, whiteSpace: 'nowrap' }}>
                            {meta}
                          </Text>
                        )}
                      </Group>
                    }
                  />
                )
              })}
            </Timeline>
          </Box>
        )
      })}
    </Box>
  )
}
